import { MINISITE_OWNER_KEY_LENGTH, MINISITE_OWNER_KEY_SALT } from "../../@types/sharedSchema";

/**
 * The signed-in realtor's minisite key, kept in this browser for the client portal, which has no
 * sign-in (portal/src/minisite/owner.ts reads the same storage key). A minisite's answer carries its
 * realtor's key (`ownerKey`); when the two match, the portal knows the realtor is looking at their own
 * page: their visits are not counted as the buyer's, and the page offers them its editor
 * (/dashboard/minisite). A hash of the email, never the email: `MINISITE_OWNER_KEY_*` in sharedSchema,
 * computed the same way by the server (minisitePublicRules.ts).
 */
export const MINISITE_OWNER_KEY_STORAGE = "webot-minisite-owner-key";

export async function minisiteOwnerKey(email: string): Promise<string> {
  const bytes = new TextEncoder().encode(`${MINISITE_OWNER_KEY_SALT}${email.trim().toLowerCase()}`);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, MINISITE_OWNER_KEY_LENGTH);
}

/** Called on every sign-in state: the account's key while someone is signed in, nothing after. */
export function rememberMinisiteOwner(email: string | null | undefined): void {
  try {
    if (!email) {
      localStorage.removeItem(MINISITE_OWNER_KEY_STORAGE);
      return;
    }
    if (!globalThis.crypto?.subtle) return;
    minisiteOwnerKey(email)
      .then((key) => localStorage.setItem(MINISITE_OWNER_KEY_STORAGE, key))
      .catch(() => undefined);
  } catch {
    // Storage unavailable: the portal simply counts this browser as a visitor.
  }
}
